'use client';

import { type PropsWithChildren, createContext, useContext } from 'react';

import {
  useAccessToken,
  useSlug
} from 'reservations/account/providers/ConfigProvider';
import ReservationNotFoundError from 'reservations/base/components/ReservationNotFoundError';
import { BookingState, BookingType } from 'reservations/booking/constants';
import { useBookingQuery } from 'reservations/booking/queries/useBookingQuery';
import { useBookingQueryV2 } from 'reservations/booking/queries/useBookingQueryV2';
import type { BookingQueryResponse } from 'reservations/booking/types';

const BookingContext = createContext<BookingQueryResponse | null>(null);

type BookingProviderProps = {
  bookingId: string;
  hasVisitId?: boolean;
};

export const BookingProvider = ({
  bookingId,
  hasVisitId = true,
  children
}: PropsWithChildren<BookingProviderProps>) => {
  const accessToken = useAccessToken();
  const slug = useSlug();
  const {
    data: dataV1,
    isPending: isPendingV1,
    isError: isErrorV1
  } = useBookingQuery(accessToken, bookingId, hasVisitId);
  const {
    data: dataV2,
    isPending: isPendingV2,
    isError: isErrorV2
  } = useBookingQueryV2(bookingId, hasVisitId);
  const data = hasVisitId ? dataV2 : dataV1;
  const isPending = hasVisitId ? isPendingV2 : isPendingV1;
  const isError = hasVisitId ? isErrorV2 : isErrorV1;

  if (isPending) {
    return <></>;
  }

  if (isError) {
    return <ReservationNotFoundError slug={slug} />;
  }

  return (
    <BookingContext.Provider value={data!}>{children}</BookingContext.Provider>
  );
};

export const useBooking = () => {
  const context = useContext(BookingContext);
  if (context == null) {
    throw new Error('useBooking must be used within BookingProvider');
  }

  const {
    //TODO: Update variable name to isEnrolledInCheckless across the repo
    opt_in_checkless: optInChecklessPayments,
    saved_payment_method: savedPaymentMethod,
    tip_percentage: tipPercentage
  } = context.visit ?? {};

  return {
    ...context,
    checkless: { optInChecklessPayments, tipPercentage },
    savedPaymentMethod: {
      hasCardOnFile: !!savedPaymentMethod,
      id: savedPaymentMethod?.id,
      cardExpMonth: savedPaymentMethod?.card_expiration_month,
      cardExpYear: savedPaymentMethod?.card_expiration_year,
      cardLast4: savedPaymentMethod?.card_last4,
      cardType: savedPaymentMethod?.card_type
    },
    fee: context?.fee ?? null
  };
};

export const useBookingDetails = () => useBooking()?.booking;

export const useBookingEmail = () => useBookingDetails()?.email;

export const useBookingId = () => useBookingDetails()?.bookingId;

export const useBookingVisit = () => useBooking()?.visit;

export const useBookingGuests = () => useBooking()?.guests;

export const useInvitedGuests = () =>
  useBookingGuests()?.filter(({ lead }) => !lead);

export const useNumberOfGuests = () => useBookingGuests()?.length ?? 1;

export const useNumberOfGuestsInvitedToSplit = () =>
  useBookingGuests()?.filter(
    ({ check_splitting_enabled }) => check_splitting_enabled
  ).length ?? 1;

export const useNumberOfGuestsSplitting = () =>
  useBookingGuests()?.filter(({ opt_in_checkless }) => opt_in_checkless)
    .length ?? 0;

export const useIsBillSplit = () => useNumberOfGuestsInvitedToSplit() > 1;

export const useIsLead = () => useBookingVisit()?.lead;

export const useSavedPaymentMethod = () =>
  useBookingVisit()?.saved_payment_method;

export const useIsCheckSplittingEnabled = () =>
  useBookingVisit()?.check_splitting_enabled;

export const useIsAttending = () => useBookingVisit()?.attending;

const useBookingState = () => useBookingDetails()?.state;

const useBookingType = () => useBookingDetails()?.type;

export const useIsCanceled = () => {
  const state = useBookingState();
  return (
    state === BookingState.CANCELED ||
    state === BookingState.NO_SHOW ||
    state === BookingState.DELETED
  );
};

export const useIsActive = () => {
  const state = useBookingState();
  return state === BookingState.ACTIVE || state === BookingState.PENDING;
};

export const useHasStarted = () => useBookingState() === BookingState.SEATED;

export const useIsWaitlist = () => useBookingType() === BookingType.WAITPARTY;

export const useIsReservation = () =>
  useBookingType() === BookingType.RESERVATION;

export const useBookingFee = () => useBooking()?.fee;

export const useBookingHasFee = () => useBookingFee()?.shouldCollect;

export const useBookingChecklessPayment = () => useBooking()?.checkless;

export const useBookingPaymentMethod = () => useBooking()?.savedPaymentMethod;

export const useExternalUrl = () => {
  const externalUrl = useBookingDetails()?.externalReservationUrl;
  const hasExternalUrl = !!externalUrl;
  const redirectToExternalUrl = () => window.open(externalUrl, '_blank');
  return { hasExternalUrl, redirectToExternalUrl };
};
